import React, { useEffect, useState, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * IndexTicker — a continuously left-scrolling market ticker for the public
 * Landing Page, styled like the strips on major financial platforms.
 *
 * Data source: the public `global_state/market_prices` Firestore document
 * (rule: `allow read: if true`). The central scheduler refreshes it ~every 30s,
 * so the ticker stays live with ZERO extra API cost and NO auth required —
 * perfect for unauthenticated visitors.
 *
 * %change is computed against `_previousClose[symbol]` written by the scheduler.
 */

// Which symbols to surface, in display order. `key` is the field in
// market_prices; `label` is what visitors see.
// NOTE: SPY/QQQ/DIA/SMH are tracked as the tradeable ETF proxies — the central
// scheduler stores the ETF price, NOT the underlying index points (SPY ≈ SPX/10,
// DIA ≈ Dow/100, etc.). We therefore label them with the actual ETF ticker so
// the price is always honest to what's shown (matches brokerage convention).
const INDICES = [
  { key: 'BTCUSD', label: 'BTC', prefix: '$' },
  { key: 'ETHUSD', label: 'ETH', prefix: '$' },
  { key: 'XAUUSD', label: 'GOLD', prefix: '$' },
  { key: 'SPY', label: 'SPY', prefix: '$' },
  { key: 'QQQ', label: 'QQQ', prefix: '$' },
  { key: 'DIA', label: 'DIA', prefix: '$' },
  { key: 'SMH', label: 'SMH', prefix: '$' },
  { key: 'VIX', label: 'VIX', prefix: '' },
  { key: 'USD/THB', label: 'USD/THB', prefix: '฿' },
];

const fmtPrice = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  const decimals = n >= 1000 ? 0 : 2;
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const IndexTicker = ({ themeMode = 'dark' }) => {
  const dk = themeMode === 'dark';
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'global_state', 'market_prices'),
      (snap) => { if (snap.exists()) setPrices(snap.data()); },
      () => { /* permission/network error → ticker simply stays hidden */ }
    );
    return unsub;
  }, []);

  const items = useMemo(() => {
    if (!prices) return [];
    const prev = prices._previousClose || {};
    return INDICES.map((cfg) => {
      const priceStr = fmtPrice(prices[cfg.key]);
      if (priceStr == null) return null;
      const cur = Number(prices[cfg.key]);
      const pc = Number(prev[cfg.key]);
      let pct = null;
      if (Number.isFinite(pc) && pc > 0) pct = ((cur - pc) / pc) * 100;
      return { label: cfg.label, price: `${cfg.prefix}${priceStr}`, pct };
    }).filter(Boolean);
  }, [prices]);

  // Nothing to show yet (no data / all zero) → render nothing, no layout jump.
  if (items.length === 0) return null;

  // Duplicate the list so the -50% translate loop is seamless.
  const loop = [...items, ...items];

  const upC = 'text-emerald-400';
  const dnC = 'text-rose-400';

  return (
    <>
      <style>{`
        @keyframes pp-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .pp-ticker-track { animation: pp-ticker 45s linear infinite; }
        .pp-ticker-wrap:hover .pp-ticker-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .pp-ticker-track { animation: none; }
          .pp-ticker-wrap { overflow-x: auto; }
        }
      `}</style>
      <div
        className={`pp-ticker-wrap fixed bottom-0 left-0 right-0 z-40 h-9 overflow-hidden border-t backdrop-blur-xl ${
          dk ? 'bg-[#06061a]/90 border-white/10' : 'bg-white/90 border-slate-200'
        }`}
        aria-label="Live market prices"
      >
        <div className="pp-ticker-track flex items-center whitespace-nowrap h-full w-max">
          {loop.map((it, i) => {
            const up = it.pct != null && it.pct >= 0;
            return (
              <div key={i} className="flex items-center gap-2 px-5 h-full border-r border-white/5">
                <span className={`text-[11px] font-bold tracking-wide ${dk ? 'text-slate-200' : 'text-slate-700'}`}>{it.label}</span>
                <span className={`text-[11px] font-semibold tabular-nums ${dk ? 'text-slate-300' : 'text-slate-600'}`}>{it.price}</span>
                {it.pct != null && (
                  <span className={`text-[10px] font-bold tabular-nums ${up ? upC : dnC}`}>
                    {up ? '▲' : '▼'} {Math.abs(it.pct).toFixed(2)}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Edge fades for a polished, "endless" look */}
        <div className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r ${dk ? 'from-[#06061a]' : 'from-white'} to-transparent`} />
        <div className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l ${dk ? 'from-[#06061a]' : 'from-white'} to-transparent`} />
      </div>
    </>
  );
};

export default IndexTicker;
